// @owners { team: patients-team }
import {
  Body,
  Button,
  Column,
  Description,
  InlineAlert,
  LoadingEmptyState,
  MdSpacing,
  SecondaryPage,
  XlPadding,
  XlSpacing,
} from '@alto/design-system';
import { InsuranceCard, useQueryInsuranceConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { setEditingInsuranceID } from '~shared/actions/onboarding';
import {
  INSURANCE_FOUND,
  INSURANCE_INPUT,
  INSURANCE_INPUT_MANUAL,
  INSURANCE_INPUT_PHOTO,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance as InsuranceType } from '~shared/types/clients';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import {
  nextUrl as getNextUrl,
  previousUrl as getOnboardingPreviousUrl,
} from '~web/features/onboarding/helpers/urlHandler';

export const OnboardingInsuranceFoundPage = () => {
  const dispatch = useDispatchShared();
  const { navigate } = useNavigation();
  const location = useLocation();
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const { insurances, isLoading: fetchInsurancesLoading, error: fetchInsurancesError } = useGetInsurances();
  const photoUrl = getNextUrl({
    currentRoute: INSURANCE_FOUND,
    desiredRoute: INSURANCE_INPUT_PHOTO,
  });
  const manualUrl = getNextUrl({
    currentRoute: INSURANCE_FOUND,
    desiredRoute: INSURANCE_INPUT_MANUAL,
  });
  const addInsuranceUrl = getNextUrl({
    currentRoute: INSURANCE_FOUND,
    desiredRoute: INSURANCE_INPUT,
  });
  const nextUrl = getNextUrl({ currentRoute: INSURANCE_FOUND });
  const currentRoute = routeFromPath(location.pathname);
  const onboardingPreviousUrl = getOnboardingPreviousUrl({ currentRoute });
  const titleText = showProgyny
    ? 'Along with your Progyny benefits, we found your prescription insurance!'
    : 'We found your prescription insurance!';
  const subtitleText = showProgyny
    ? 'This is separate from your Progyny benefits and may help us find additional savings on medications not covered by Progyny.'
    : 'Have additional prescription benefits or savings cards? Add them so we can get you the best pricing.';

  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryInsuranceConfigs();

  const handleEditInsurance = (insurance: InsuranceType) => {
    const url = insurance.image_url ? photoUrl : manualUrl;
    dispatch(setEditingInsuranceID(insurance.id));

    navigate(url);
  };

  const handleAddInsuranceClick = () => {
    navigate(addInsuranceUrl);
  };

  const handleConfirm = () => {
    navigate(nextUrl);
  };

  const handleBack = () => {
    if (onboardingPreviousUrl) {
      navigate(onboardingPreviousUrl);
    }
  };

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      withoutWebFramingElementHeights
      buttons={
        !fetchInsurancesError && !fetchInsurancesLoading && !isFetching
          ? [
              <Button
                type="tertiary"
                key="confirm"
                label="Add another benefit"
                onPress={handleAddInsuranceClick}
              />,
              <Button
                type="primary"
                key="confirm"
                label="Continue"
                onPress={handleConfirm}
              />,
            ]
          : []
      }
      footerPlacementContext="webScreenWithoutPolicyFooter"
      onDismiss={handleBack}
      dismissIcon="chevronleft"
      headerBackgroundColor={headerBackgroundColor}
      HeaderContent={
        <OnboardingHeader
          title={titleText}
          subtitle={<Body center>{subtitleText}</Body>}
        />
      }
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          partnershipName={partnershipName}
        />
      }
    >
      {fetchInsurancesLoading ? <LoadingEmptyState /> : null}
      {fetchInsurancesError && !fetchInsurancesLoading ? (
        <>
          <InlineAlert type="error">
            <Description>Looks like we're having some technical difficulty. Please refresh this page.</Description>
          </InlineAlert>
          <XlSpacing />
        </>
      ) : null}
      {!fetchInsurancesLoading && !fetchInsurancesError && (
        <Column>
          {insurances.map((insurance, index) => (
            <Fragment key={`insurance-card-${insurance.id}`}>
              <InsuranceCard
                insurance={insurance}
                onEditInsurance={handleEditInsurance}
              />
              {index !== insurances.length - 1 && <MdSpacing />}
            </Fragment>
          ))}
        </Column>
      )}
    </SecondaryPage>
  );
};
